'use client'

import type { ComponentType } from 'react'
import { HeroClassic } from './HeroClassic'
import { HeroVibrant } from './HeroVibrant'
import { HeroEditorial } from './HeroEditorial'
import { HeroDark } from './HeroDark'
import { HeroBold } from './HeroBold'
import { HeroCosta } from './HeroCosta'
import { HeroCafeto } from './HeroCafeto'
import { HeroSumi } from './HeroSumi'
import { HeroRuta } from './HeroRuta'
import { HeroDeco } from './HeroDeco'
import { HeroJardin } from './HeroJardin'
import { HeroBarro } from './HeroBarro'
import { HeroOliva } from './HeroOliva'
import { HeroNeon } from './HeroNeon'
import { HeroMercado } from './HeroMercado'
import { HeroVino } from './HeroVino'
import { HeroMedianoche } from './HeroMedianoche'
import { HeroObsidiana } from './HeroObsidiana'
import { HeroBrasa } from './HeroBrasa'
import { HeroSplit } from './HeroSplit'
import { HeroMarca } from './HeroMarca'
import type { HeroVariant } from '@/lib/microsite/themes'
import type { OpenStatus } from '@/lib/hours/openStatus'

/** Props que recibe todo hero. Cada hero declara el subconjunto que usa
 *  (p.ej. solo bold pinta logoUrl); los extras se ignoran. */
export type HeroProps = {
  businessName: string
  tagline?: string
  heroImageUrl?: string
  heroVideoUrl?: string
  logoUrl?: string
  /** CTA principal del hero: lleva a la sección del menú (#menu). */
  onViewMenu: () => void
  /** Abre el popup de delivery. El botón solo se muestra si hasDelivery. */
  onDelivery?: () => void
  hasDelivery?: boolean
  openStatus?: OpenStatus | null
  city?: string | null
  pickupWaitMinutes?: number
  /** Texto del "Listo en…" ya resuelto (`waitText(…, "readyIn")`, PR K). Sin él,
   *  el hero arma el de siempre con `pickupWaitMinutes`. */
  readyInLabel?: string
}

/** Cada tema tiene su hero dedicado. El Record sobre HeroVariant obliga en
 *  typecheck a que toda variante nueva de themes.ts tenga hero enrutado. */
export const HERO_BY_VARIANT: Record<HeroVariant, ComponentType<HeroProps>> = {
  classic: HeroClassic,
  vibrant: HeroVibrant,
  editorial: HeroEditorial,
  dark: HeroDark,
  bold: HeroBold,
  costa: HeroCosta,
  cafeto: HeroCafeto,
  sumi: HeroSumi,
  ruta: HeroRuta,
  deco: HeroDeco,
  jardin: HeroJardin,
  barro: HeroBarro,
  oliva: HeroOliva,
  neon: HeroNeon,
  mercado: HeroMercado,
  vino: HeroVino,
  medianoche: HeroMedianoche,
  obsidiana: HeroObsidiana,
  brasa: HeroBrasa,
  split: HeroSplit,
  marca: HeroMarca,
}

type Props = HeroProps & { heroVariant?: HeroVariant }

export function HeroSection({ heroVariant = 'classic', ...heroProps }: Props) {
  const Hero = HERO_BY_VARIANT[heroVariant] ?? HeroClassic
  return <Hero {...heroProps} />
}
